import { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { FILE_ONLY_PLACEHOLDER } from "../types/chat";
import type {
  ConnectionState,
  OutgoingMessage,
  ServerMessage,
  DocumentAttachment,
} from "../types/chat";
import { useAuthStore } from "../stores/auth-store";
import { useChatStore } from "../stores/chat-store";
import { attemptRefresh } from "../lib/refresh-mutex";
import { getUserErrorMessage } from "../lib/error-messages";
import { env } from "../lib/env";
import { useToolAnimation } from "./useToolAnimation";
import { Permissions } from "../types/permissions";

const AI_WS_URL = env.AI_WS_URL;
const MAX_RETRIES = 5;

export function useChatSocket() {
  const navigate = useNavigate();
  const wsRef = useRef<WebSocket | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("connecting");
  const retriesRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const connectRef = useRef<() => void>(null);
  const navigateRef = useRef(navigate);
  const lastPingRef = useRef(0);
  const watchdogRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );
  const hasConnectedRef = useRef(false);
  // Tracks whether auth handshake completed for this socket instance
  const isAuthenticatedRef = useRef(false);
  // Tracks whether the tab is currently hidden — used to pause the watchdog
  const tabHiddenRef = useRef(document.hidden);

  const toolAnimation = useToolAnimation();
  // Ref for stable access in ws.onmessage callback
  const toolAnimRef = useRef(toolAnimation);
  useEffect(() => {
    toolAnimRef.current = toolAnimation;
  }, [toolAnimation]);
  const toolCompletedRef = useRef(false);

  const storeThreadId = useChatStore((s) => s.threadId);

  useEffect(() => {
    toolAnimation.abort();
    toolCompletedRef.current = false;
  }, [storeThreadId]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const connect = useCallback(() => {
    isAuthenticatedRef.current = false;
    const { accessToken, permissions } = useAuthStore.getState();
    if (!accessToken) return;
    if (!permissions.includes(Permissions.AI_CHAT)) return;

    // New auth pattern: connect without token in URL, send auth message on open.
    const ws = new WebSocket(
      `${AI_WS_URL}/api/ai/v1/ws/chatbot/linkedin`
    );

    ws.onopen = () => {
      console.log("[WS] connection opened, sending auth");
      // Send auth as the very first message (must arrive within 10s or server closes 1008)
      ws.send(JSON.stringify({ action: "auth", token: accessToken }));

      lastPingRef.current = Date.now();

      // Ping-based watchdog: server sends ping every ~30s.
      // If we go 65s without a ping, consider the connection stale.
      // Paused while the tab is hidden (browsers throttle timers in background).
      watchdogRef.current = setInterval(() => {
        if (ws !== wsRef.current) {
          clearInterval(watchdogRef.current);
          return;
        }
        // Pause timeout enforcement while tab is hidden to avoid false triggers
        if (tabHiddenRef.current) {
          // Nudge lastPingRef forward so the elapsed counter doesn't accumulate
          // while the browser throttles our timers in the background.
          lastPingRef.current = Date.now();
          return;
        }
        const elapsed = Math.round((Date.now() - lastPingRef.current) / 1000);
        console.log("[WS] watchdog check — last ping", elapsed, "s ago");
        if (Date.now() - lastPingRef.current > 65_000) {
          console.warn(
            "[WS] Heartbeat timeout — no ping for",
            elapsed,
            "s — forcing reconnect"
          );
          ws.close(4000, "Heartbeat timeout");
        }
      }, 10_000);
    };

    ws.onmessage = (event) => {
      let data: ServerMessage;
      try {
        data = JSON.parse(event.data as string);
      } catch {
        console.error("Failed to parse WebSocket message:", event.data);
        return;
      }
      const store = useChatStore.getState();

      switch (data.type) {
        case "authenticated":
          console.log("[WS] authenticated successfully");
          isAuthenticatedRef.current = true;
          hasConnectedRef.current = true;
          retriesRef.current = 0;
          setConnectionState("connected");
          break;

        case "ping":
          // Server heartbeat — no pong required, just update timestamp
          console.log(
            "[WS] ping received, interval:",
            Math.round((Date.now() - lastPingRef.current) / 1000),
            "s"
          );
          lastPingRef.current = Date.now();
          break;

        case "tool_calling":
          console.log(
            "[WS] tool_calling:",
            data.tool_name,
            "| toolCompleted:",
            toolCompletedRef.current,
            "| animActive:",
            toolAnimRef.current.state.active
          );
          toolCompletedRef.current = false;
          toolAnimRef.current.start(data.tool_name);
          break;

        case "stream": {
          const currentThreadId = useChatStore.getState().threadId;
          if (data.thread_id && data.thread_id !== currentThreadId) break;
          // Complete tool animation only when actual content arrives (not null thinking tokens)
          if (!toolCompletedRef.current && data.messages) {
            console.log(
              "[ToolAnim] stream → calling complete() | toolCompleted:",
              toolCompletedRef.current,
              "| hasMessages:",
              !!data.messages
            );
            if (import.meta.env.DEV) {
              console.log(
                "[WS] stream (first after tool):",
                data.messages.slice(0, 50)
              );
            }
            toolCompletedRef.current = true;
            toolAnimRef.current.complete();
          } else if (!toolCompletedRef.current) {
            console.log(
              "[ToolAnim] stream → skipping complete() (no messages) | toolCompleted:",
              toolCompletedRef.current,
              "| hasMessages:",
              !!data.messages
            );
          }
          // Backend sends null while AI is thinking; skip to keep "Thinking..." pill visible
          if (data.messages) {
            store.appendStreamToken(data.messages);
          }
          break;
        }

        case "turn_complete": {
          const currentThreadId = useChatStore.getState().threadId;
          if (data.thread_id && data.thread_id !== currentThreadId) break;
          // Complete tool animation if still active
          console.log(
            "[ToolAnim] turn_complete → toolCompleted:",
            toolCompletedRef.current,
            "| calling complete():",
            !toolCompletedRef.current
          );
          if (!toolCompletedRef.current) {
            toolCompletedRef.current = true;
            toolAnimRef.current.complete();
          }
          const hasCandidates =
            (data.artifact?.candidates_list_with_scores?.candidates_id_list
              ?.length ?? 0) > 0;
          console.log(
            "[WS] turn_complete, thread:",
            data.thread_name,
            "has_candidates:",
            hasCandidates
          );
          const jdPath = hasCandidates
            ? (data.artifact?.job_description_s3_key ?? null)
            : null;
          store.completeTurn(data.messages ?? "", data.thread_name, jdPath);
          break;
        }

        case "stopped": {
          console.log("[WS] stopped");
          console.log(
            "[ToolAnim] stopped → toolCompleted:",
            toolCompletedRef.current,
            "| calling complete():",
            !toolCompletedRef.current
          );
          if (!toolCompletedRef.current) {
            toolCompletedRef.current = true;
            toolAnimRef.current.complete();
          }
          store.stopGeneration();
          break;
        }

        case "paused":
          console.log("[WS] paused");
          break;

        case "resumed":
          console.log("[WS] resumed");
          break;

        case "timeout":
          console.log("[WS] timeout — pause exceeded limit");
          // Treat timeout as a stream stop from the user's perspective
          store.stopGeneration();
          break;

        case "error":
          console.error("[WS] error:", data.details);
          toolAnimRef.current.abort();
          // Connection stays open on error — just surface the error in UI
          store.setError(getUserErrorMessage(data.details), data.details);
          break;
      }
    };

    ws.onclose = (event) => {
      console.log(
        "[WS] connection closed, code:",
        event.code,
        "reason:",
        event.reason,
        "wasAuthenticated:",
        isAuthenticatedRef.current
      );
      toolAnimRef.current.abort();
      clearInterval(watchdogRef.current);
      isAuthenticatedRef.current = false;
      wsRef.current = null;

      // Reset streaming state so UI is not permanently stuck
      const store = useChatStore.getState();
      if (store.isStreaming) {
        store.resetStreaming();
      }

      if (event.code === 1008) {
        // Permission denial (e.g., CET role lacks ai:chat) — never recoverable via refresh.
        // Don't loop; the user just shouldn't be opening this socket.
        const reason = event.reason || "";
        if (/restricted|permission|forbidden/i.test(reason)) {
          return;
        }
        // Try silent token refresh before redirecting
        attemptRefresh().then((success) => {
          if (success) {
            retriesRef.current = 0;
            connectRef.current?.();
          } else {
            useAuthStore.getState().clearAuth();
            navigateRef.current("/login");
          }
        });
        return;
      }

      if (retriesRef.current < MAX_RETRIES) {
        // Only show "reconnecting" banner if we've successfully connected before
        if (hasConnectedRef.current) {
          setConnectionState("reconnecting");
        }
        const delay = Math.min(1000 * 2 ** retriesRef.current, 30_000);
        retriesRef.current++;
        console.log(
          "[WS] reconnecting in",
          delay,
          "ms (attempt",
          retriesRef.current,
          "of",
          MAX_RETRIES,
          ")"
        );
        reconnectTimerRef.current = setTimeout(
          () => connectRef.current?.(),
          delay
        );
      } else {
        // Max retries exhausted — always show "Unable to connect" banner
        console.warn("[WS] max retries exhausted, giving up");
        setConnectionState("disconnected");
      }
    };

    ws.onerror = () => {
      console.error("[WS] connection error");
    };

    wsRef.current = ws;
  }, []); // Stable: uses refs and Zustand .getState() only

  // Keep ref in sync so onclose can call the latest connect (self-reference)
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const sendMessage = useCallback(
    (
      text: string,
      documents?: DocumentAttachment[],
      mentionedProject?: {
        projectId: string;
        clientId: string;
        location?: string;
        threadId?: string;
      }
    ) => {
      if (
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN ||
        !isAuthenticatedRef.current
      )
        return;

      const { user } = useAuthStore.getState();
      const { threadId, addUserMessage, addThinkingPlaceholder } =
        useChatStore.getState();

      if (!user) return;

      // Optimistic UI — store actual text (may be empty for file-only messages)
      addUserMessage(text, documents);
      addThinkingPlaceholder();

      // Wire format — backend requires non-empty text
      const wireText = text || FILE_ONLY_PLACEHOLDER;

      // Strip frontend-only silent flag before sending — backend must never see it
      const wireDocuments = documents?.map(
        ({ silent: _silent, ...doc }) => doc
      );

      const payload: OutgoingMessage = {
        user_id: user.id,
        thread_id: threadId,
        message: [{ type: "text", text: wireText }],
        ...(wireDocuments?.length && { documents: wireDocuments }),
        ...(mentionedProject && {
          project_id: mentionedProject.projectId,
          client_id: mentionedProject.clientId,
          ...(mentionedProject.location && { location: mentionedProject.location }),
          ...(mentionedProject.threadId && { thread_id: mentionedProject.threadId }),
        }),
      };

      wsRef.current.send(JSON.stringify(payload));
    },
    [] // Stable: uses refs and Zustand .getState() only
  );

  const resendLastMessage = useCallback(() => {
    const {
      lastUserInput,
      removeLastErrorMessage,
      addThinkingPlaceholder,
      threadId,
      isStreaming,
    } = useChatStore.getState();
    if (
      !lastUserInput ||
      isStreaming ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      !isAuthenticatedRef.current
    )
      return;

    const { user } = useAuthStore.getState();
    if (!user) return;

    // Remove error bubble and show thinking placeholder
    removeLastErrorMessage();
    addThinkingPlaceholder();

    // Wire format
    const wireText = lastUserInput.text || FILE_ONLY_PLACEHOLDER;

    const payload: OutgoingMessage = {
      user_id: user.id,
      thread_id: threadId,
      message: [{ type: "text", text: wireText }],
      ...(lastUserInput.documents?.length && {
        documents: lastUserInput.documents,
      }),
    };

    wsRef.current.send(JSON.stringify(payload));
  }, []); // Stable: uses refs and Zustand .getState() only

  const stopGeneration = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ action: "stop" }));
  }, []); // Stable: uses refs only

  const sendPause = useCallback(() => {
    if (
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      !isAuthenticatedRef.current
    )
      return;
    wsRef.current.send(JSON.stringify({ action: "pause" }));
  }, []); // Stable: uses refs only

  const sendResume = useCallback(() => {
    if (
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      !isAuthenticatedRef.current
    )
      return;
    wsRef.current.send(JSON.stringify({ action: "resume" }));
  }, []); // Stable: uses refs only

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    clearInterval(watchdogRef.current);
    toolAnimRef.current.abort();
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CONNECTING) {
      wsRef.current.close();
    }
    wsRef.current = null;
  }, []);

  // Tab visibility: pause watchdog while hidden, reconnect if the WS dropped
  // while the tab was backgrounded (browsers may kill idle connections).
  useEffect(() => {
    const handleVisibilityChange = () => {
      const hidden = document.hidden;
      tabHiddenRef.current = hidden;

      if (!hidden) {
        console.log("[WS] tab became visible");
        // Nudge lastPingRef so the watchdog doesn't immediately trigger
        lastPingRef.current = Date.now();

        // If the socket is gone while we were hidden, reconnect now
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          console.log("[WS] tab visible — reconnecting after background drop");
          retriesRef.current = 0;
          connectRef.current?.();
        }
      } else {
        console.log("[WS] tab hidden — pausing watchdog");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []); // stable: uses only refs

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connectionState,
    connected: connectionState === "connected",
    sendMessage,
    resendLastMessage,
    stopGeneration,
    sendPause,
    sendResume,
    disconnect,
    toolAnimation: toolAnimation.state,
  };
}
